import {
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Avatar,
  Chip,
  useTheme,
  Stack,
  SxProps,
  Theme,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useState } from "react";
import { tokens } from "../../../theme";
import type { OpsCrewAvailability } from "../types/opsDashboard";

type AllocationStatus = "Available" | "On Dispatch" | "Off Duty";

interface FieldTechItem {
  id: string;
  name: string;
  role: string;
  profileImage: string | null;
  status: AllocationStatus;
}

const chipStyles: Record<AllocationStatus, { bgcolor: string; color: string }> = {
  Available: { bgcolor: "#e8f7f0", color: "#10b981" },
  "On Dispatch": { bgcolor: "#e6f0ff", color: "#007bff" },
  "Off Duty": { bgcolor: "#f3f4f6", color: "#6b7280" },
};

function mapAvailability(a: string): AllocationStatus {
  if (a === "on_stream") return "On Dispatch";
  if (a === "available") return "Available";
  return "Off Duty";
}

interface CrewAvailabilityWidgetProps {
  sx?: SxProps<Theme>;
  crewAvailability?: OpsCrewAvailability;
}

export default function CrewAvailabilityWidget({
  sx,
  crewAvailability,
}: CrewAvailabilityWidgetProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [tabIndex, setTabIndex] = useState(0);

  const radius = 50;
  const strokeWidth = 14;
  const circumference = 2 * Math.PI * radius;

  // Use API counts when available, fall back to computing from sample
  const total = crewAvailability?.total ?? 0;
  const available = crewAvailability?.available ?? 0;
  const onDispatch = crewAvailability?.on_stream ?? 0;
  const offDuty = crewAvailability?.off_duty ?? 0;

  const availableRatio = total ? available / total : 0;
  const onDispatchRatio = total ? onDispatch / total : 0;
  const offDutyRatio = total ? offDuty / total : 0;

  const crewList: FieldTechItem[] = (crewAvailability?.sample ?? []).map((c) => ({
    id: String(c.id),
    name: `${c.first_name} ${c.last_name}`.trim(),
    role: c.roles[0] ?? "",
    profileImage: c.profile_image,
    status: mapAvailability(c.availability),
  }));

  const filteredCrew = crewList.filter((crew) => {
    if (tabIndex === 0) return true;
    if (tabIndex === 1) return crew.status === "Available";
    if (tabIndex === 2) return crew.status === "On Dispatch";
    return crew.status === "Off Duty";
  });

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "24px",
        backgroundColor: colors.primary[900],
        width: "100%",
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight={700} color={colors.primary[100]}>
          Crew Availability
        </Typography>
        <MoreHorizIcon sx={{ color: colors.primary[400], cursor: "pointer" }} />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
          px: 1,
        }}
      >
        {/* Donut Chart */}
        <Box
          sx={{
            position: "relative",
            width: 130,
            height: 130,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="130" height="130" viewBox="0 0 130 130">
            <g transform="rotate(-90 65 65)">
              <circle
                cx="65" cy="65" r={radius}
                fill="transparent"
                stroke="#10b981"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - availableRatio)}
              />
              <circle
                cx="65" cy="65" r={radius}
                fill="transparent"
                stroke="#007bff"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - onDispatchRatio)}
                transform={`rotate(${360 * availableRatio} 65 65)`}
              />
              <circle
                cx="65" cy="65" r={radius}
                fill="transparent"
                stroke={`${colors.primary[500]}60`}
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - offDutyRatio)}
                transform={`rotate(${360 * (availableRatio + onDispatchRatio)} 65 65)`}
              />
            </g>
          </svg>
          <Box sx={{ position: "absolute", textAlign: "center" }}>
            <Typography variant="h4" fontWeight={800} color={colors.primary[100]} sx={{ lineHeight: 1 }}>
              {total}
            </Typography>
            <Typography variant="caption" fontWeight={700} color={colors.primary[400]} sx={{ fontSize: "0.65rem" }}>
              CREW
            </Typography>
          </Box>
        </Box>

        {/* Legend */}
        <Stack spacing={0.5} sx={{ flex: 1, maxWidth: "150px" }}>
          {[
            { label: "Available", count: available, color: "#10b981" },
            { label: "On Dispatch", count: onDispatch, color: "#007bff" },
            { label: "Off Duty", count: offDuty, color: `${colors.primary[500]}60` },
          ].map(({ label, count, color }) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
                <Typography variant="caption" fontWeight={600} color={colors.primary[300]}>
                  {label}
                </Typography>
              </Box>
              <Typography variant="caption" fontWeight={700} color={colors.primary[100]}>
                {count}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Filter Tabs */}
      <Box sx={{ bgcolor: colors.primary[800], borderRadius: "12px", p: 0.5, mb: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, val) => setTabIndex(val)}
          variant="fullWidth"
          sx={{ minHeight: "32px", "& .MuiTabs-indicator": { display: "none" } }}
        >
          {["All", "Available", "Dispatched", "Off"].map((lbl) => (
            <Tab
              key={lbl}
              label={lbl}
              disableRipple
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "none",
                minHeight: "32px",
                py: 0.5,
                borderRadius: "8px",
                color: colors.primary[400],
                "&.Mui-selected": {
                  bgcolor: colors.primary[900],
                  color: colors.primary[100],
                  boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
                },
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Crew List */}
      <Stack spacing={1.5}>
        {filteredCrew.length === 0 ? (
          <Typography variant="body2" color={colors.primary[400]} textAlign="center" py={1}>
            No crew members in this category
          </Typography>
        ) : (
          filteredCrew.map((crew) => (
            <Box
              key={crew.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                bgcolor: colors.primary[800],
                borderRadius: "16px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  src={crew.profileImage ?? undefined}
                  sx={{ width: 36, height: 36, bgcolor: colors.primary[500], fontSize: "0.8rem" }}
                >
                  {crew.name.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={700} color={colors.primary[100]}>
                    {crew.name}
                  </Typography>
                  <Typography variant="caption" color={colors.primary[400]} display="block">
                    {crew.role || "—"}
                  </Typography>
                </Box>
              </Box>

              <Chip
                label={crew.status}
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  borderRadius: "6px",
                  ...chipStyles[crew.status],
                }}
              />
            </Box>
          ))
        )}
      </Stack>
    </Paper>
  );
}
